// The corpus that JoshLM-7B "knows about Harsh."
// Hand-curated chunks: ~1-3 sentences each, tagged for the composer.
//
// Each chunk is independently retrievable. Aim for:
//   - Specific, citable facts (numbers, names, dates)
//   - Discrete project descriptions
//   - Philosophy snippets in Harsh's own voice
//   - Common visitor-intent answers (hire, contact, schedule)
//
// More chunks = more coverage but more noise. ~80 is the sweet spot.

export type Chunk = {
  id: string;
  text: string;
  category:
    | "identity"
    | "career"
    | "project"
    | "open-source"
    | "philosophy"
    | "skills"
    | "contact"
    | "education"
    | "writing"
    | "hire"
    | "meta";
  tags: string[];
  href?: string; // optional canonical link to cite
};

export const CORPUS: Chunk[] = [
  // ───────────── identity & current ─────────────
  {
    id: "identity-1",
    text: "Harsh Joshi is a software engineer in Bengaluru with about five years across application backends, distributed systems, UI platforms, and applied AI. He's currently a senior engineer at Coupang.",
    category: "identity",
    tags: ["who", "harsh", "engineer", "bengaluru", "coupang", "senior"],
  },
  {
    id: "identity-2",
    text: "He likes building small things that work and big things that scale. Sometimes they're the same thing.",
    category: "philosophy",
    tags: ["philosophy", "tagline", "small", "big", "scale"],
  },
  {
    id: "identity-3",
    text: "I grew up in open source. Contributing to projects, writing code in public, Google Summer of Code, open-source communities. I was full-stack before Claude Code made everyone full-stack.",
    category: "philosophy",
    tags: ["open source", "philosophy", "gsoc", "full-stack", "claude code"],
  },
  {
    id: "identity-4",
    text: "Harsh's current focus: applied AI, developer tools, consumer AI apps, and the building blocks that will power tomorrow's AI infrastructure.",
    category: "philosophy",
    tags: ["current", "focus", "applied ai", "developer tools", "consumer ai", "infrastructure"],
  },

  // ───────────── coupang (current) ─────────────
  {
    id: "coupang-1",
    text: "He's been at Coupang since October 2025 as a senior software engineer, working on the distributed warehouse-management systems that move inventory across more than a hundred fulfillment centers.",
    category: "career",
    tags: ["coupang", "current", "senior engineer", "wms", "warehouse"],
  },
  {
    id: "coupang-2",
    text: "Those systems handle a few million daily orders for around 20 million users, with almost everything getting to customers inside 24 hours.",
    category: "career",
    tags: ["coupang", "scale", "delivery", "sla", "users", "orders"],
  },

  // ───────────── early-stage ai startup ─────────────
  {
    id: "startup-1",
    text: "He spent six months in 2025 on the founding team of an early-stage AI startup, as a forward-deployed AI engineer. It was an \"AI operating partner\" for regulated enterprises — basically the same playbook OpenAI runs as AI Deployment Engineering.",
    category: "career",
    tags: ["startup", "founding", "forward deployed", "fde", "ai operating partner", "openai", "deployment"],
  },
  {
    id: "startup-2",
    text: "He embedded with Cleartrip (Flipkart's travel arm), Bharatsure (embedded-insurance APIs, Insurtech of the Year 2024), 100ms (live video and healthcare AI), and Putting Scene — diagnosing workflows, prototyping agents, and shipping production AI with evals and human-in-the-loop.",
    category: "career",
    tags: ["cleartrip", "bharatsure", "100ms", "putting scene", "clients", "production ai"],
  },

  // ───────────── visa ─────────────
  {
    id: "visa-1",
    text: "From March 2024 to October 2025 he was a senior software engineer at Visa, on the Authorize.Net flagship payment gateway — 500+ TPS, around 445,000 merchants, and roughly \\$200 billion a year. He also shipped the unified onboarding flow adopted by Bank of America.",
    category: "career",
    tags: ["visa", "authorize.net", "payments", "gateway", "tps", "merchants", "200b", "bank of america"],
  },
  {
    id: "visa-2",
    text: "While at Visa he ran a side bet of 0→1 applied-AI experiments inside Authorize.Net: developer-productivity agents, intelligent QA automation, AI-driven churn analysis, and MCP-based agentic tooling — all aligned with Visa's Intelligent Commerce direction.",
    category: "career",
    tags: ["visa", "applied ai", "mcp", "agentic", "intelligent commerce", "experiments"],
  },

  // ───────────── plum ─────────────
  {
    id: "plum-1",
    text: "He joined Plum as the founding engineer in January 2021 and stayed three years, writing the literal first line of code for policy purchases, enrollments, retail insurance, and health benefits.",
    category: "career",
    tags: ["plum", "founding engineer", "founding", "first line of code", "insurance", "health"],
    href: "https://plumhq.com",
  },
  {
    id: "plum-2",
    text: "He was the sole engineer on v1 of Plum's iOS app — designing, building, and shipping it end-to-end to the App Store.",
    category: "career",
    tags: ["plum", "ios app", "sole engineer", "app store", "swift"],
    href: "https://apps.apple.com/in/app/plum-health-benefits/id1616851078",
  },
  {
    id: "plum-3",
    text: "He also led architecture for Plum's health-benefits marketplace — 500K+ users, 4K+ enterprises, and a 50% lift in adoption. That platform now backs roughly 6,000 companies and 600,000 insured lives.",
    category: "career",
    tags: ["plum", "marketplace", "health benefits", "scale", "architecture"],
  },
  {
    id: "plum-4",
    text: "At Plum, spent enough time with claims and policies to learn how Indian insurance actually works under the hood.",
    category: "philosophy",
    tags: ["plum", "claims", "indian insurance", "philosophy"],
  },

  // ───────────── qoala ─────────────
  {
    id: "qoala-1",
    text: "On the side, he consulted with Qoala — a Southeast-Asian insure-tech with about 50,000 agents and a million users — as a GenAI consultant on their AI products.",
    category: "career",
    tags: ["qoala", "genai", "consultant", "southeast asia", "insurtech"],
    href: "https://qoala.app",
  },
  {
    id: "qoala-2",
    text: "For Qoala specifically: he shipped a GenAI motor-inspection platform doing 10,000+ inspections a month, designed their first AutoGen agentic cross-sell system, and built out an LLM gateway with multimodal pipelines and human review.",
    category: "career",
    tags: ["qoala", "motor inspection", "autogen", "agentic", "llm gateway", "multimodal", "hitl"],
  },

  // ───────────── ReallyArtificial.org ─────────────
  {
    id: "ra-1",
    text: "ReallyArtificial.org is the open-source org Harsh started in 2024 to build the boring-but-essential parts of agent infrastructure — testing, memory, gateways, human approval, agent definitions. Everything's MIT-licensed.",
    category: "open-source",
    tags: ["reallyartificial", "really artificial", "oss", "open source", "agent infrastructure"],
    href: "https://reallyartificial.org",
  },
  {
    id: "ra-2",
    text: "MCP-JEST: the first eval/test framework for Model Context Protocol servers. Like Jest, but for MCP servers. Published on npm under the ReallyArtificial org.",
    category: "open-source",
    tags: ["mcp-jest", "mcpjest", "mcp", "eval", "testing", "jest", "framework"],
    href: "https://www.npmjs.com/package/mcp-jest",
  },
  {
    id: "ra-3",
    text: "aa-kit: an MCP-native Python agent framework on PyPI. Every agent becomes an MCP server. Multi-model fallback, pluggable memory, ReAct + chain-of-thought reasoning patterns.",
    category: "open-source",
    tags: ["aa-kit", "aakit", "python", "agent framework", "pypi", "mcp"],
    href: "https://pypi.org/project/aa-kit/",
  },
  {
    id: "ra-4",
    text: "Engram: agent memory library. Retain → recall → reflect cycle, hybrid search (BM25 + vector), entity resolution. The memory layer most agents skip.",
    category: "open-source",
    tags: ["engram", "memory", "agent memory", "retain", "recall", "reflect"],
    href: "https://github.com/reallyartificial/engram",
  },
  {
    id: "ra-5",
    text: "Approval Protocol: human-in-the-loop framework for AI agents, with LangChain integration. What MCP did for tool access, AP does for human oversight.",
    category: "open-source",
    tags: ["approval protocol", "hitl", "human in the loop", "langchain"],
    href: "https://github.com/reallyartificial/approvalprotocol",
  },
  {
    id: "ra-6",
    text: "Freeport: open-source multi-provider LLM gateway. Routing, fallback, semantic caching. The gateway most teams build then never open source.",
    category: "open-source",
    tags: ["freeport", "llm gateway", "routing", "fallback", "semantic caching"],
    href: "https://github.com/reallyartificial/freeport",
  },
  {
    id: "ra-7",
    text: "Grain: structured agent-definition format. .grain.yaml files with personality vectors, channel overrides, schema-validated. Framework-agnostic.",
    category: "open-source",
    tags: ["grain", "agent definition", "yaml", "personality", "schema"],
    href: "https://grain.reallyartificial.org",
  },

  // ───────────── friday (separate agent) ─────────────
  {
    id: "friday-1",
    text: "Friday (@fridayjoshi) is a separate autonomous AI agent Harsh built to help maintain ReallyArtificial.org. Friday has its own GitHub identity, commits code, and acts as co-maintainer. Not to be confused with this site's agent.",
    category: "open-source",
    tags: ["friday", "fridayjoshi", "@fridayjoshi", "autonomous", "co-maintainer"],
    href: "https://github.com/fridayjoshi",
  },

  // ───────────── shipped apps ─────────────
  {
    id: "tongue-1",
    text: "Tongue: an iOS voice-AI language tutor, built with SwiftUI. Live on the App Store. Harsh shipped it solo as a consumer-AI bet.",
    category: "project",
    tags: ["tongue", "ios", "swiftui", "voice ai", "language tutor", "app store", "consumer ai"],
    href: "https://apps.apple.com/us/app/tongueapp/id6759001870",
  },
  {
    id: "lockbox-1",
    text: "Lockbox: an iOS credentials manager built with SwiftUI. Live on the App Store.",
    category: "project",
    tags: ["lockbox", "ios", "swiftui", "credentials", "password manager", "app store"],
    href: "https://apps.apple.com/us/app/lockboxapp/id6747517926",
  },
  {
    id: "paper-1",
    text: "Paper: cross-platform Flutter document scanner with real-time edge detection. Open source.",
    category: "project",
    tags: ["paper", "flutter", "document scanner", "edge detection", "mobile"],
    href: "https://github.com/josharsh/paper",
  },

  // ───────────── developer tools (subdomains) ─────────────
  {
    id: "quark-1",
    text: "Quark: a system of AI agents that validates product ideas with cited evidence. 'Think before you build.' Real sources, evidence-backed convictions for product thinkers and entrepreneurs.",
    category: "project",
    tags: ["quark", "product validation", "ai agents", "research", "ideas"],
    href: "https://quark.josharsh.com",
  },
  {
    id: "otterly-1",
    text: "Otterly: exposes Claude as an OpenAI-compatible local server. Use your Claude Code subscription as if it were an OpenAI API — no API keys, no per-token billing.",
    category: "project",
    tags: ["otterly", "claude", "openai compatible", "local server", "claude code"],
    href: "https://otterly.josharsh.com",
  },
  {
    id: "xeli-1",
    text: "Xeli: Excel for the terminal — interactive TUI spreadsheet with AI-powered queries. Edit cells, run formulas, ask natural-language questions about data.",
    category: "project",
    tags: ["xeli", "terminal", "tui", "spreadsheet", "excel", "ai queries"],
    href: "https://xeli.josharsh.com",
  },
  {
    id: "claudecto-1",
    text: "Claude CTO: visual dashboard for managing Claude Code sessions. If Claude Code is your engineer, claudecto is your CTO. Search, browse, manage Claude sessions across projects.",
    category: "project",
    tags: ["claude cto", "claudecto", "dashboard", "claude code", "sessions"],
    href: "https://claudecto.josharsh.com",
  },
  {
    id: "ai-terminal-1",
    text: "The AI Terminal Course: 14 hands-on terminal-native AI modules. Understand what your AI tools are actually doing under the hood.",
    category: "project",
    tags: ["ai terminal course", "terminal", "course", "modules", "education"],
    href: "https://www.aiterminalcourse.com/",
  },
  {
    id: "mcp-creator-1",
    text: "MCP Creator: turns any API into an MCP server. Paste your OpenAPI spec, get a working MCP server.",
    category: "project",
    tags: ["mcp creator", "mcp", "api", "openapi", "server generator"],
    href: "https://creator.themcp.ai",
  },

  // ───────────── npm packages ─────────────
  {
    id: "npm-1",
    text: "Harsh has published 40+ npm packages as @josharsh. Includes agent tooling (mcp-jest, omnimcp, ensure-json, otterly), older bits (genai-hooks, logi-logger), and various utilities.",
    category: "open-source",
    tags: ["npm", "packages", "josharsh", "40 packages", "ecosystem"],
    href: "https://npmjs.com/~josharsh",
  },
  {
    id: "100lines-1",
    text: "100LinesOfCode: a GitHub repo of small, useful tools — each under a hundred lines. Picked up around 500 stars over the years.",
    category: "open-source",
    tags: ["100linesofcode", "github", "stars", "small tools", "snippets"],
    href: "https://github.com/josharsh/100linesofCode",
  },
  {
    id: "ensurejson-1",
    text: "ensure-json: a 3kB dependency-free helper that repairs 'almost-JSON' text from LLMs and returns a valid JavaScript object. Zod-validated. Used in production by Harsh's agent stack.",
    category: "open-source",
    tags: ["ensure-json", "json", "llm", "zod", "repair"],
    href: "https://www.npmjs.com/package/ensure-json",
  },
  {
    id: "omnimcp-1",
    text: "OmniMCP: universal Model Context Protocol client + playground. Connect any MCP server, debug interactions, build tooling.",
    category: "open-source",
    tags: ["omnimcp", "mcp", "client", "playground"],
    href: "https://www.npmjs.com/package/omnimcp",
  },

  // ───────────── education & gsoc ─────────────
  {
    id: "edu-1",
    text: "B.Tech, Computer Science Engineering from UPES Dehradun, 2017–2021. 9.03 CGPA. University Silver Medal for Academic Performance.",
    category: "education",
    tags: ["education", "btech", "computer science", "upes", "dehradun", "cgpa", "silver medal"],
  },
  {
    id: "gsoc-1",
    text: "Google Summer of Code 2020: contributed to Submitty, an open-source LMS used by a New York university with around 10,000 students. Built peer-grading and auto-grading evaluation features — probably where Harsh first got hooked on evals.",
    category: "education",
    tags: ["gsoc", "google summer of code", "submitty", "lms", "peer grading", "auto grading", "evals"],
    href: "https://summerofcode.withgoogle.com/archive/2020/organizations/5702344097923072",
  },
  {
    id: "ms-partner-1",
    text: "Microsoft Student Partner, 2019–2020. Speaker at Developer Week 2024. Open Source Instructor at KodeKloud.",
    category: "education",
    tags: ["microsoft student partner", "developer week", "kodekloud", "speaker", "community"],
  },
  {
    id: "research-1",
    text: "Published 'Correspondence of Natural & Artificial Cognition in Classifier Systems' in IJSER, Volume 11, 2020. Studies the relation between intuitive reasoning and artificial cognition through classification tasks.",
    category: "education",
    tags: ["research", "ijser", "paper", "cognition", "classifier", "publication"],
  },

  // ───────────── writing ─────────────
  {
    id: "writing-1",
    text: "Unengineered: Harsh's Substack. Short essays on engineering, AI, and product. Tagline: 'Simpler words for an otherwise complicated world.'",
    category: "writing",
    tags: ["unengineered", "substack", "blog", "writing", "essays"],
    href: "https://unengineered.substack.com",
  },
  {
    id: "writing-2",
    text: "Harsh named the blog Unengineered because the hardest part of engineering was never engineering.",
    category: "philosophy",
    tags: ["unengineered", "philosophy", "engineering", "hardest part"],
  },
  {
    id: "writing-3",
    text: "Recent Unengineered posts: 'Thick vs Thin agent harness', 'The 2023 vs 2025 gateway problem'.",
    category: "writing",
    tags: ["unengineered", "posts", "thick thin", "gateway", "agent harness"],
  },

  // ───────────── machine learning / applied ai story ─────────────
  {
    id: "ml-1",
    text: "Harsh has done applied ML, not pure research. His published paper studies classification tasks comparing natural and artificial cognition. He's also built GAN-based AI art and PSO graph-routing projects.",
    category: "skills",
    tags: ["machine learning", "ml", "applied ai", "classifier", "gan", "research", "pso"],
  },
  {
    id: "ml-2",
    text: "Most of Harsh's recent ML work is applied: LLM orchestration, agentic systems, evals, multimodal pipelines at Qoala, MCP-based agentic tooling at Visa. He's an engineer who deploys models in production, not a pure ML researcher.",
    category: "skills",
    tags: ["machine learning", "applied", "production", "llm", "agents", "deploy", "engineer"],
  },

  // ───────────── skills ─────────────
  {
    id: "skills-ai",
    text: "AI/Agents skill stack: MCP, agentic systems, evals & benchmarks, RAG, LLM orchestration, AutoGen, OTel GenAI, HITL, agent memory.",
    category: "skills",
    tags: ["ai", "agents", "mcp", "evals", "rag", "llm", "autogen", "skills"],
  },
  {
    id: "skills-langs",
    text: "Programming languages Harsh works in: Python, TypeScript, Java, Swift, SQL, Dart, Node.js.",
    category: "skills",
    tags: ["languages", "python", "typescript", "java", "swift", "sql", "dart", "node"],
  },
  {
    id: "skills-fe",
    text: "Frontend and mobile: React, Next.js, SwiftUI, Flutter, Tailwind.",
    category: "skills",
    tags: ["frontend", "mobile", "react", "nextjs", "swiftui", "flutter", "tailwind"],
  },
  {
    id: "skills-be",
    text: "Backend and infrastructure: FastAPI, Kafka, Redis, Spring, AWS, GCP, Docker, microservices, DDD, event-driven systems.",
    category: "skills",
    tags: ["backend", "infra", "fastapi", "kafka", "redis", "spring", "aws", "gcp", "docker", "microservices", "ddd"],
  },

  // ───────────── hire & openai ─────────────
  {
    id: "hire-1",
    text: "He's actively looking at OpenAI roles right now — specifically AI Deployment Engineer, Forward-Deployed Engineer, or Solutions Engineer for the Startups segment. SF, NYC, Singapore, Sydney, London, Paris, India Remote, Tokyo, and Dublin all work for him.",
    category: "hire",
    tags: ["hire", "openai", "ai deployment engineer", "forward deployed", "fde", "solutions engineer", "startups", "looking"],
  },
  {
    id: "hire-2",
    text: "The Munich opening is the one exception — it requires fluent German, which Harsh doesn't have. For any other location he's open to relocating for the right role.",
    category: "hire",
    tags: ["munich", "german", "relocation"],
  },
  {
    id: "hire-3",
    text: "The short case for Harsh as an AI Deployment Engineer: his forward-deployed founding-team stint is the closest analog to the role anyone applies with — six months forward-deployed with four regulated enterprises. ReallyArtificial.org is rare open-source agent infrastructure. Plum gives him founding-engineer scale (600K insured lives). Visa gives him production scale (\\$200B+ annualised). And Tongue proves he can solo-ship consumer AI to the App Store.",
    category: "hire",
    tags: ["openai", "hire", "case", "why harsh", "ai deployment", "fit"],
  },

  // ───────────── contact ─────────────
  {
    id: "contact-1",
    text: "The fastest way to reach him is email: harsh.joshi.pth@gmail.com. He replies quickly.",
    category: "contact",
    tags: ["email", "contact", "reach out", "harsh.joshi.pth"],
  },
  {
    id: "contact-2",
    text: "He's also at linkedin.com/in/josharsh, github.com/josharsh, npmjs.com/~josharsh, and writes at unengineered.substack.com.",
    category: "contact",
    tags: ["linkedin", "github", "npm", "substack", "social", "profiles"],
  },
  {
    id: "contact-3",
    text: "Want to chat? Email harsh.joshi.pth@gmail.com with a topic and he'll reply. If you're a recruiter, lead with the role and the team.",
    category: "contact",
    tags: ["chat", "meeting", "call", "schedule", "recruiter"],
  },

  // ───────────── linkedin headline ─────────────
  {
    id: "headline-1",
    text: "Harsh's positioning: 5+ years of technology-agnostic hands-on engineering experience. Application backends, distributed systems, UI platforms, and applied AI systems.",
    category: "identity",
    tags: ["headline", "positioning", "linkedin", "5 years", "experience"],
  },
  {
    id: "headline-2",
    text: "He's built 0-to-1 at a 10-person startup, scaled systems from one to many, and shipped inside enterprises processing billions of transactions a year and serving at least half the world.",
    category: "identity",
    tags: ["0 to 1", "startup", "scale", "enterprise", "half the world", "billions"],
  },
  {
    id: "headline-3",
    text: "Harsh keeps ending up in the same place — close to real problems, simple curiosities, and simpler ideas. Sometimes, making complex systems work for the next person.",
    category: "philosophy",
    tags: ["philosophy", "real problems", "curiosities", "next person"],
  },

  // ───────────── android & mobile (the missing breadth) ─────────────
  {
    id: "android-1",
    text: "Yes — Harsh has built Android apps. His undergraduate project Calo (\"Healthy U\") was an Android health app with calorie detection from food images, heart-rate monitoring via the phone's LED sensor, blood pressure tracking, drug detection via OCR, Firebase auth, and material-design UI for Android API 21+.",
    category: "project",
    tags: ["android", "calo", "healthy u", "mobile", "java", "kotlin", "firebase", "ocr", "computer vision"],
  },
  {
    id: "android-2",
    text: "Beyond Calo, Harsh has formal Android training (LinkedIn Learning: Android Development Essential Training, Design a User Interface with Java). Most of his recent mobile work has shifted to SwiftUI (Tongue, Lockbox) and Flutter (Paper), but the Android foundation is there.",
    category: "skills",
    tags: ["android", "mobile development", "java", "kotlin", "linkedin learning", "certification"],
  },
  {
    id: "mobile-1",
    text: "Harsh works across mobile platforms: native iOS with SwiftUI (Tongue, Lockbox, Plum's first iOS app), Android (Calo, training), and cross-platform with Flutter (Paper document scanner).",
    category: "skills",
    tags: ["mobile", "ios", "android", "flutter", "swiftui", "cross-platform", "swift", "dart"],
  },

  // ───────────── language-specific chunks ─────────────
  {
    id: "lang-python",
    text: "Python is one of Harsh's two strongest languages. He uses it for AI work (aa-kit is a Python framework on PyPI), backend services (FastAPI, Django, Flask experience), data work, ML, and CLI tools. Python with type hints for any PyPI packages he ships.",
    category: "skills",
    tags: ["python", "fastapi", "django", "flask", "pypi", "ai", "backend"],
  },
  {
    id: "lang-typescript",
    text: "TypeScript is Harsh's other primary language. Used across his React/Next.js frontend work, Node.js services, and most of his npm packages (40+ as @josharsh). Properly typed exports, Zod for runtime validation.",
    category: "skills",
    tags: ["typescript", "node.js", "react", "next.js", "npm", "frontend", "backend"],
  },
  {
    id: "lang-java",
    text: "Java is Harsh's enterprise language. Spring at Visa for payment-gateway services. Android development with Java (Calo). Used heavily in production backend systems at Visa scale.",
    category: "skills",
    tags: ["java", "spring", "android", "visa", "enterprise", "backend"],
  },
  {
    id: "lang-swift",
    text: "Swift and SwiftUI — Harsh ships iOS apps in Swift. Tongue (voice-AI tutor), Lockbox (credentials manager), and Plum's v1 iOS app (which he shipped as the sole engineer end-to-end).",
    category: "skills",
    tags: ["swift", "swiftui", "ios", "apple", "mobile"],
  },
  {
    id: "lang-go-rust",
    text: "Harsh uses Go and Rust with help from Claude. They're not his strongest languages, but he's productive in them — explaining non-obvious patterns is just part of the workflow.",
    category: "skills",
    tags: ["go", "golang", "rust", "claude", "polyglot"],
  },
  {
    id: "lang-sql",
    text: "SQL — Harsh works in Postgres (Plum, Coupang), MongoDB, MySQL. Modeling, query optimisation, migrations. Not exotic, but production-grounded.",
    category: "skills",
    tags: ["sql", "postgres", "postgresql", "mongodb", "mysql", "database", "nosql"],
  },

  // ───────────── more on each project: technical depth ─────────────
  {
    id: "tongue-2",
    text: "Tongue's technical stack: SwiftUI for UI, on-device audio processing, OpenAI Whisper-style speech-to-text, LLM-driven feedback loop, persistent learner-progress storage on device. Solo build, App Store shipping.",
    category: "project",
    tags: ["tongue", "ios", "swiftui", "whisper", "speech to text", "stt", "voice", "audio"],
  },
  {
    id: "plum-tech-1",
    text: "Plum's technical stack at the founding: Node.js + TypeScript backend, React + TypeScript frontend, Postgres, microservices, AWS infrastructure. Harsh wrote the early architecture and saw it through to 6K companies.",
    category: "career",
    tags: ["plum", "node.js", "typescript", "react", "postgres", "aws", "microservices", "stack"],
  },
  {
    id: "visa-tech-1",
    text: "At Visa Authorize.Net, the stack involved Java Spring services, payment-gateway middleware, Kafka for event streams, Redis caching, and enterprise observability. Plus the layer of applied-AI experiments Harsh ran on top: MCP servers, agentic tooling, dev-productivity agents.",
    category: "career",
    tags: ["visa", "java", "spring", "kafka", "redis", "payments", "observability", "stack"],
  },
  {
    id: "coupang-tech-1",
    text: "At Coupang, Harsh works on warehouse-management systems — distributed event-driven services across 100+ fulfillment centers. The stack includes Java, distributed systems patterns (event sourcing, CQRS-ish flows), and high-throughput orchestration for inbound and inventory.",
    category: "career",
    tags: ["coupang", "java", "wms", "warehouse", "distributed", "event-driven", "high throughput"],
  },

  // ───────────── earlier projects + history ─────────────
  {
    id: "crio-1",
    text: "Crio.Do (Jan-Apr 2020): Harsh built backend architecture for three software products — a secure file-sharing system and a stock portfolio analyzer among them. Collaborated with 100+ developers in work-like product-development simulation.",
    category: "career",
    tags: ["crio", "crio.do", "backend", "file sharing", "stock portfolio", "early career"],
  },
  {
    id: "haldwani-1",
    text: "HaldwaniConnect (2016-2018): Harsh's first founding project — an initiative to give online identities to local businesses, firms, and workers in Haldwani. Forum-based communication, redressal of community problems. Built in high school.",
    category: "project",
    tags: ["haldwani", "haldwaniconnect", "community", "high school", "early project"],
  },
  {
    id: "gans-1",
    text: "Harsh worked on Generative Adversarial Networks (GANs) for AI Art generation during undergrad. Used statistical measures like the golden ratio to improve discriminator learning. Pure-research ML rather than applied.",
    category: "skills",
    tags: ["gan", "generative adversarial", "ai art", "research", "ml", "machine learning"],
  },
  {
    id: "pso-1",
    text: "Network Route Optimization using Particle Swarm Optimization — Harsh's undergrad project comparing computer-network routing strategies and optimising via swarm intelligence. Solved multi-destination routing where traditional graph algorithms (Kruskal's, Prim's) struggle.",
    category: "skills",
    tags: ["pso", "particle swarm", "graph algorithms", "routing", "optimization", "research"],
  },

  // ───────────── infra & ops chunks ─────────────
  {
    id: "infra-1",
    text: "Cloud and infrastructure: Harsh works in AWS (Plum, Visa, Coupang), GCP (Crio.Do, side projects), Docker, Kubernetes-adjacent orchestration. Holds a Udacity Cloud Developer Nanodegree on AWS.",
    category: "skills",
    tags: ["aws", "gcp", "cloud", "docker", "kubernetes", "udacity", "nanodegree"],
  },
  {
    id: "infra-2",
    text: "Distributed systems is a recurring theme: event-driven, pub/sub, multi-tenancy, sharding patterns. Practical experience at Visa (500+ TPS, 445K merchants) and Coupang (100+ FCs, millions of orders/day).",
    category: "skills",
    tags: ["distributed systems", "event driven", "pub sub", "multi tenant", "sharding", "scale"],
  },
  {
    id: "testing-1",
    text: "Testing is a deep interest: Harsh built MCP-JEST, the first eval framework for MCP servers. Approach: integration tests over unit tests when the boundary is an API; make tests that actually catch bugs.",
    category: "skills",
    tags: ["testing", "evals", "integration tests", "mcp-jest", "tdd"],
  },
  {
    id: "observability-1",
    text: "Observability and analytics: Harsh has built OTel-based instrumentation, agent observability (agent-obs uses OTel GenAI semantic conventions), and logging libraries (logi-logger).",
    category: "skills",
    tags: ["observability", "otel", "opentelemetry", "logging", "monitoring", "agent-obs"],
  },

  // ───────────── community & talks ─────────────
  {
    id: "talks-1",
    text: "Speaking and teaching: Harsh spoke at Developer Week 2024 on agent infrastructure. He's an Open Source Instructor at KodeKloud (course on open-source development) and a Microsoft Learn Ambassador alumnus.",
    category: "education",
    tags: ["talks", "speaking", "developer week", "kodekloud", "microsoft learn", "instructor"],
  },
  {
    id: "open-community-1",
    text: "OPEN Community: Harsh was Collaboration Manager (2018-2020) and Lead Student Developer earlier — cross-functional team contributing to conceptualizing organisational structure for open source in India.",
    category: "education",
    tags: ["open community", "open source india", "collaboration", "student leader"],
  },

  // ───────────── more philosophy chunks (his voice) ─────────────
  {
    id: "philosophy-1",
    text: "His approach to shipping: ship first, polish later. Get something functional, then iterate for structure. Don't gold-plate v1. Then once it's working, structure the code so the next engineer — human or AI — can move faster. Clear separation of concerns, well-named functions.",
    category: "philosophy",
    tags: ["philosophy", "ship", "shipping", "ship first", "iterate", "approach", "building", "v1"],
  },
  {
    id: "philosophy-2",
    text: "On dependencies and frameworks: prefer libraries over frameworks where control matters later. Minimal dependencies. Standard library when it's good enough. Flat-ish folder structures — don't nest five levels deep.",
    category: "philosophy",
    tags: ["philosophy", "dependencies", "libraries", "frameworks", "minimal"],
  },
  {
    id: "philosophy-3",
    text: "Testing philosophy: test core business logic and data transformations. Don't test UI glue code or framework boilerplate. Integration tests over unit tests when the boundary is an API. Tests should actually catch bugs.",
    category: "philosophy",
    tags: ["philosophy", "testing", "tests", "integration", "unit", "approach"],
  },
  {
    id: "philosophy-4",
    text: "On AI: \"I'm not an AI believer who has to be an AI believer.\" Harsh approaches AI as an engineer — what actually works, what's hype, what's useful. The Unengineered blog explores this honestly.",
    category: "philosophy",
    tags: ["ai", "philosophy", "honest", "skeptical", "engineering"],
  },

  // ───────────── what he doesn't do ─────────────
  {
    id: "gaps-1",
    text: "Harsh isn't a pure ML researcher — he doesn't publish at NeurIPS, doesn't train foundation models. His ML work is applied: deploying models in production, building infra around them, evaluating them.",
    category: "skills",
    tags: ["limitations", "applied", "vs research", "ml researcher", "honest"],
  },
  {
    id: "gaps-2",
    text: "Languages Harsh doesn't speak fluently: spoken German (which is why he skipped OpenAI's Munich role). Programming: he uses Go and Rust with Claude's help rather than from memory. C++ exists in his past but isn't a daily driver.",
    category: "skills",
    tags: ["limitations", "german", "go", "rust", "c++", "honest"],
  },

  // ───────────── meta — about this site / agent ─────────────
  {
    id: "meta-1",
    text: "This site is built with React, Vite, TypeScript, Tailwind, and shadcn/ui. The agent on /ai runs entirely in your browser — no backend calls.",
    category: "meta",
    tags: ["site", "stack", "react", "vite", "typescript", "tailwind"],
  },
  {
    id: "meta-2",
    text: "JoshLM-7B is the satirical brand name for the retrieval system you're talking to. The actual implementation is BM25 + a 22MB embedding model running in your browser. Click the ? button in the trace panel to see the architecture.",
    category: "meta",
    tags: ["joshlm", "satire", "bm25", "embedding", "real architecture", "meta"],
  },
  {
    id: "meta-3",
    text: "The trace panel on the right shows every action the agent takes — bm25Search, embeddingRerank, composeResponse. Real algorithms, real timings, no hidden API calls. This is what most 'AI' production systems actually do under the marketing layer.",
    category: "meta",
    tags: ["trace", "transparent", "honest", "architecture"],
  },
];

// Merge hand-curated chunks with the auto-generated GitHub chunks.
// The hand-curated set anchors voice and philosophy; GitHub adds breadth.
import { GITHUB_CORPUS } from "./corpus-github";

export const FULL_CORPUS: Chunk[] = [...CORPUS, ...GITHUB_CORPUS];

// Convenience: a flat searchable text index for BM25 to consume.
// Combines both sets so retrieval covers everything.
export function corpusForBM25() {
  return FULL_CORPUS.map((c) => ({
    id: c.id,
    text: `${c.text} ${c.tags.join(" ")}`,
  }));
}
